import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import DotsVertical from 'components/icons/dotsVertical';
import FloatingFilterButton from 'components/FloatingFilterButton';
import TaskStatusPicker from 'components/TaskStatusPicker';
import TaskWithStatus from 'components/TaskWithStatus';
import TasksOpened from 'components/TasksOpened';
import { TaskStatus } from 'types';
import { TASK_EVENTS } from 'analytics/posthog/types';
import useDebouncedTrack from 'hooks/useDebouncedTrack';
import {
    Actions, Container, Header, TasksListContainer, Title,
} from './styled';

const tasksStatusSwitchOptions = [
    { name: 'open', label: 'Open' },
    { name: 'solved', label: 'Solved' },
    { name: 'archived', label: 'Archived' },
];

const TasksList = () => {
    const { agentId = '', taskStatus = 'open' } = useParams<{ agentId: string; taskStatus: TaskStatus }>();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const { debounceTrack: trackSearch } = useDebouncedTrack(500);

    const showOpenTasks = useMemo(() => taskStatus.trim().toLowerCase() === 'open', [taskStatus]);

    useEffect(() => {
        trackSearch(TASK_EVENTS.LIST_VIEWED, { status: taskStatus });
    }, []);

    useEffect(() => {
        if (searchQuery.length > 0) {
            trackSearch(TASK_EVENTS.SEARCHED, { query: searchQuery, resultsCount: 0 });
        }
    }, [searchQuery, trackSearch]);

    const switchStatusFilter = useCallback(
        (status: string) => {
            trackSearch(TASK_EVENTS.FILTER_BY_STATUS, { status });
            navigate(`/${agentId}/tasks/${status}`);
        },
        [agentId, navigate, trackSearch],
    );

    return (
        <Container>
            <Header>
                <Title>Tasks</Title>
                <Actions>
                    <FloatingFilterButton
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                    <TaskStatusPicker
                        buttonIcon={<DotsVertical />}
                        taskStatuses={tasksStatusSwitchOptions}
                        value={taskStatus}
                        onChange={switchStatusFilter}
                    />
                </Actions>
            </Header>
            <TasksListContainer>
                <RenderIf isTrue={showOpenTasks}>
                    <TasksOpened
                        searchQuery={searchQuery}
                    />
                </RenderIf>
                <RenderIf isTrue={!showOpenTasks}>
                    <TaskWithStatus
                        status={taskStatus as Exclude<TaskStatus, 'open'>}
                        searchQuery={searchQuery}
                    />
                </RenderIf>
            </TasksListContainer>
        </Container>
    );
};

export default TasksList;
